'use client'

import { useState, useRef, useCallback, ReactElement } from 'react'

interface Ripple {
  id: number
  x: number
  y: number
}

interface UseRippleOptions {
  size?: number
  duration?: number
}

interface UseRippleReturn {
  createRipple: (event: React.MouseEvent<HTMLElement>) => void
  rippleElements: ReactElement[]
}

/**
 * Hook for creating material-design style ripple effects on click
 *
 * @example
 * ```tsx
 * const { createRipple, rippleElements } = useRipple()
 *
 * return (
 *   <button onClick={createRipple} className="relative overflow-hidden">
 *     Click me
 *     {rippleElements}
 *   </button>
 * )
 * ```
 */
export function useRipple(options: UseRippleOptions = {}): UseRippleReturn {
  const { size = 30, duration = 600 } = options
  const [ripples, setRipples] = useState<Ripple[]>([])
  const rippleIdRef = useRef(0)

  const createRipple = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const element = event.currentTarget
      const rect = element.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const id = rippleIdRef.current++

      setRipples(prev => [...prev, { id, x, y }])

      setTimeout(() => {
        setRipples(prev => prev.filter(ripple => ripple.id !== id))
      }, duration)
    },
    [duration],
  )

  const halfSize = size / 2
  const rippleElements = ripples.map(ripple => (
    <span
      key={ripple.id}
      className="animate-ripple pointer-events-none absolute rounded-full bg-black/10"
      style={{
        left: ripple.x - halfSize,
        top: ripple.y - halfSize,
        width: size,
        height: size,
      }}
    />
  ))

  return { createRipple, rippleElements }
}
